import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ParentLayout from '../components/ParentLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, CheckCircle2, MessageSquare, RefreshCw, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminRosterNotesPage = () => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [resolvingId, setResolvingId] = useState(null);
  const [error, setError] = useState('');

  const fetchNotes = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API_URL}/api/admin/roster-notes?status=${statusFilter}`, { withCredentials: true });
      setNotes(r.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotes(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (noteId) => {
    setResolvingId(noteId);
    try {
      await axios.put(`${API_URL}/api/admin/roster-notes/${noteId}/resolve`, {}, { withCredentials: true });
      await fetchNotes();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to resolve');
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <ParentLayout>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4" data-testid="admin-roster-notes-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="font-display text-2xl font-black text-navy uppercase tracking-tight flex items-center">
                <MessageSquare className="w-6 h-6 mr-2" /> Roster Notes
              </h1>
              <p className="text-sm text-gray-600">Coach and Team Mom flags raised against rostered players.</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchNotes} data-testid="refresh-notes">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        <div className="flex space-x-2">
          {['open', 'resolved', 'all'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-${s}`}
              className={`px-3 py-1 rounded-md text-sm font-semibold transition-colors ${statusFilter === s ? 'bg-navy text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center">
            <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : notes.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">No {statusFilter === 'all' ? '' : statusFilter} notes.</p>
        ) : (
          notes.map(n => (
            <Card key={n.note_id} data-testid={`note-card-${n.note_id}`}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-base">
                  <div className="flex items-center space-x-2">
                    <Badge className="bg-navy text-white capitalize">{n.category.replace('_', ' ')}</Badge>
                    <span className="text-navy">{n.player_name || n.player_id}</span>
                    <span className="text-gray-400">•</span>
                    <span className="text-sm text-gray-600">{n.team_name || n.team_id}</span>
                  </div>
                  <Badge variant={n.status === 'resolved' ? 'outline' : 'secondary'} className={n.status === 'resolved' ? 'border-green-500 text-green-700' : 'bg-yellow-100 text-yellow-800'}>
                    {n.status}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700 whitespace-pre-wrap mb-2">{n.body}</p>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    {n.author_name || n.author_role} ({n.author_role}) — {new Date(n.created_at).toLocaleString()}
                    {n.resolved_at && <> · Resolved {new Date(n.resolved_at).toLocaleString()}</>}
                  </p>
                  {n.status === 'open' && (
                    <Button
                      size="sm"
                      onClick={() => resolve(n.note_id)}
                      disabled={resolvingId === n.note_id}
                      className="bg-navy hover:bg-navy/90 text-white"
                      data-testid={`resolve-${n.note_id}`}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      {resolvingId === n.note_id ? 'Resolving…' : 'Mark resolved'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </ParentLayout>
  );
};

export default AdminRosterNotesPage;
